/**
 * 🎯 LE POSTE DE CHATTEUR — la seule règle, à un seul endroit.
 *
 * « Est-il en train de chatter ? » n'est PAS « a-t-il le rôle chatteur ? ».
 * Mesuré le 09-09-2026 (PR #879) : tous les chatteurs de l'org portent
 * `operator`, qui a `creators.view`, donc `kioskRoleFor` ne les reconnaît
 * JAMAIS comme chatteurs. La bonne question est « a-t-il une session de shift
 * OUVERTE côté serveur ? », et c'est `gate.sessionOuverte` qui y répond. Le
 * kiosque chatteur (slug `chatter`) reste reconnu : lui n'a pas d'autre poste.
 *
 *   enPosteDeChatteur = kiosque chatteur  OU  session de shift ouverte
 *                       OU  « Lignes » choisi par un rôle libre
 *
 * Le troisième terme est Kayla (09-09-2026 au soir) : « j'aimerais pouvoir
 * le voir aussi quand je le mets en mode liste sur mon compte bratz, sinon je
 * ne peux pas travailler ». Chez un admin ou un manager, la bascule
 * « Lignes / Cartes » n'est donc plus une simple forme de liste : « Lignes »
 * EST le poste de chatteur, tout compris — onglets, en-tête, fiche, cinq
 * onglets, disposition figée — et « Cartes » rend le Bureau. Le poste IMPOSÉ
 * (kiosque, session) se distingue du poste CHOISI pour une seule chose :
 * garder la bascule à l'écran quand on peut en sortir.
 *
 * Tout ce qui se réduit au poste de chatteur — la pile, l'en-tête allégé, la
 * fiche, les onglets plus gros — lit CET atome. Aucun lecteur ne recalcule la
 * règle : la fiche l'avait recalculée à sa façon (`kioskRoleFor ===
 * "chatter"`) et ne s'est donc jamais montrée à un seul chatteur réel.
 *
 * 🪶 Ce qui touche à la DISPOSITION — le split figé sans poignées (`Split`),
 * la bande d'onglets fan retirée (`TabStrip`), le plafond de cinq onglets
 * (`useOpenFanTab`) — lit `posteImposeAtom`, pas celui-ci. Kayla, 08-10-2026 :
 * « quand je mets en mode Lignes, après je ne peux plus avoir plusieurs
 * panneaux ». Le poste CHOISI change la forme de la liste, jamais le Bureau.
 *
 * 🪶 UNE sonde, pas un hook par lecteur. `useShiftGate` remesure l'horloge du
 * serveur (une mutation Convex toutes les cinq minutes, par instance) : un
 * hook appelé depuis chaque onglet, chaque split et chaque en-tête aurait
 * multiplié ces mesures par le nombre de panneaux ouverts. La sonde vit dans
 * `CockpitShell`, une fois ; les lecteurs lisent l'atome, gratuitement — et
 * un harnais dev le pose à la main, sans Convex.
 */
import { atom, useAtomValue, useSetAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useEffect } from "react";

import { useActiveAccountId } from "@/core/auth/activeAccount";
import { usePermissions } from "@/core/auth/usePermissions";
import { kioskRoleFor } from "@/shell/render/kioskRoles";

import { useShiftGate } from "./useShiftGate";

/** `true` quand le caller est AU POSTE DE CHATTEUR. Écrit par la sonde seule. */
export const posteDeChatteurAtom = atom(false);

/** `true` quand le poste est IMPOSÉ (kiosque chatteur, session de shift
 *  ouverte) — il n'y a alors pas de « Cartes » pour en sortir. */
export const posteImposeAtom = atom(false);

/** 👀 « Lignes » chez un rôle libre — un réglage de poste qui survit au
 *  rechargement : Kayla le choisit pour travailler, pas pour un coup d'œil.
 *  (Clé de stockage inchangée : c'était déjà celle de la bascule.) */
export const vueEnLignesAtom = atomWithStorage<boolean>("cockpit.conversations.lignes", false);

/** Lecture, depuis n'importe quel panneau. */
export function useEnPosteDeChatteur(): boolean {
  return useAtomValue(posteDeChatteurAtom);
}

/** Le poste est-il imposé (aucune bascule pour en sortir) ? */
export function usePosteImpose(): boolean {
  return useAtomValue(posteImposeAtom);
}

/**
 * La sonde — montée UNE fois par `CockpitShell`. Rend `null`.
 *
 * Le shift gate tourne déjà pour la présence (`useChatterPresence`) et pour le
 * kiosque (`useKioskLayout`) ; Convex dédoublonne la query, seule la mesure
 * d'horloge est par instance. Une de plus ici, et aucune ailleurs.
 */
export function SondeDuPosteDeChatteur() {
  const { roleSlug, permissions } = usePermissions();
  const accountId = useActiveAccountId();
  const gate = useShiftGate(accountId);
  const enKioskChatteur = kioskRoleFor(roleSlug, permissions) === "chatter";
  const vueEnLignes = useAtomValue(vueEnLignesAtom);
  const impose = enKioskChatteur || gate.sessionOuverte;
  const enPoste = impose || vueEnLignes;
  const poser = useSetAtom(posteDeChatteurAtom);
  const poserImpose = useSetAtom(posteImposeAtom);
  useEffect(() => {
    poser(enPoste);
    poserImpose(impose);
  }, [enPoste, impose, poser, poserImpose]);
  return null;
}
